import * as React from 'react';
import { format } from 'date-fns';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Box, Button, CircularProgress, useMediaQuery } from '@mui/material';
import { ServiceCategory } from '@prisma/client';
import Time from './Time';
import CustomTime from './CustomTime';
import useDayAvailabletimes from '../../../hooks/appointments/useDayAvailableTimes';
import { TakenSessionSlot } from '../../../types/appointments';
import { isOfferedSlotSelected } from '../../../utils/form/offeredSlots';

type Props = {
  selectedDay: Date;
  duration: string;
  employeeId: string;
  rescheduleAppointmentId?: string;
  timezone: string;
  onChange: (value: Date) => void;
  closeModal: any;
  setDayIsSelected: React.Dispatch<React.SetStateAction<boolean>>;
  serviceCategory?: ServiceCategory;
  customerView?: boolean;
  takenSlots?: TakenSessionSlot[];
  selectedDateTimes?: string[];
  maxSelections?: number;
};

export default function TimeList({
  selectedDay,
  duration,
  employeeId,
  timezone,
  onChange,
  closeModal,
  setDayIsSelected,
  rescheduleAppointmentId,
  serviceCategory,
  takenSlots,
  customerView,
  selectedDateTimes,
  maxSelections,
}: Props) {
  const isDesktop = useMediaQuery('(min-width:900px)');

  const {
    data: availableTimes,
    isFetching,
    isError,
  } = useDayAvailabletimes({
    day: selectedDay,
    employeeId,
    timezone,
    duration,
    rescheduleAppointmentId,
    serviceCategory,
    takenSlots,
    customerView,
  });

  const isMulti = (maxSelections ?? 1) > 1;
  const selected = selectedDateTimes ?? [];
  const atCap = isMulti && selected.length >= (maxSelections ?? 1);

  const dayHeaderFormat = 'eeee, MMMM d';

  return (
    <div className="time-box">
      <div className="time-box-header">
        {!isDesktop && (
          <Button
            variant="text"
            className="text-btn"
            style={{
              position: 'absolute',
            }}
            onClick={() => setDayIsSelected(false)}>
            Back
          </Button>
        )}
        <Typography variant="body1">
          {format(selectedDay, dayHeaderFormat)}
        </Typography>
        {isMulti && (
          <Typography variant="body2" className="time-box-meta">
            {atCap
              ? `${maxSelections} picked — remove one to swap`
              : `${selected.length} of ${maxSelections} picked`}
          </Typography>
        )}
      </div>
      <Stack spacing={1.25} className="time-stack">
        {isFetching && (
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center">
            <CircularProgress color="primary" size={24} />
          </Box>
        )}
        {isError && (
          <Typography variant="body2" className="error">
            Error loading times. Please try again
          </Typography>
        )}
        {availableTimes && availableTimes.length === 0 && !isFetching && (
          <Typography variant="body2">
            No available times on this day
          </Typography>
        )}
        {availableTimes &&
          !isFetching &&
          availableTimes.map(time => (
            <Time
              time={time}
              onChange={onChange}
              key={time.startDateTime}
              closeModal={closeModal}
              keepOpen={isMulti}
              isSelected={
                isMulti && isOfferedSlotSelected(selected, time.startDateTime)
              }
              disabled={
                atCap && !isOfferedSlotSelected(selected, time.startDateTime)
              }
            />
          ))}
        {/* Offering allows a custom time: it is checked against the artist's
            own bookings rather than their published hours */}
        {!customerView && (
          <CustomTime
            onChange={onChange}
            closeModal={isMulti ? () => {} : closeModal}
            selectedDay={selectedDay}
            disabled={atCap}
          />
        )}
      </Stack>
    </div>
  );
}
